import { z } from 'zod';

import { CommentDraftCardSchema, RecognitionDraftCardSchema, RecognitionViewCardSchema } from './bizy-ui-blocks';
import { isoDatetimeToDate } from './dates';
import { fixture } from './fixtures';

// ─── AssistantMessage Document ───────────────────────────────────────────────

// Each node is one slice of the assistant's response. All nodes share the
// `{type, payload}` envelope — markdown payloads carry `{content}`; card
// payloads carry their card-specific fields with camelCase keys preserved
// at the wire boundary.
//
// Persisted on Bizy::ChatHistory.assistant_message and streamed live via
// BizyChannel `node_token` / `node_appended` events.

export const BizyMarkdownNode = z.object({
	type: z.literal('markdown'),
	payload: z.object({ content: z.string() }),
});

export const BizyApprovalCardNode = z.object({
	type: z.literal('approval_card'),
	payload: z.record(z.string(), z.unknown()),
});

export const BizyContentCardNode = z.object({
	type: z.literal('content_card'),
	payload: z.record(z.string(), z.unknown()),
});

export const BizyDraftCardNode = z.object({
	type: z.literal('draft_card'),
	payload: z.record(z.string(), z.unknown()),
});

// Generic card rendering a vertical stack of per-person rows with per-item CTA,
// optional subtitle, and optional completion status.
const BizyMeetingPeopleCardPerson = z
	.object({
		userId: z.string().nullable().optional(),
		participantKey: z.string().nullable().optional(),
		subtitle: z.string().nullable().optional(),
		cta: z
			.object({
				label: z.string().nullable().optional(),
				href: z.string().nullable().optional(),
			})
			.partial()
			.nullable()
			.optional(),
		status: z.string().nullable().optional(),
	})
	.passthrough();

export const BizyMeetingPeopleCardNode = z.object({
	type: z.literal('meeting_people_card'),
	payload: z
		.object({
			title: z.string().nullable().optional(),
			people: z.array(BizyMeetingPeopleCardPerson).nullable().optional(),
		})
		.passthrough(),
});

export const BizyAssistantMessageNode = z.discriminatedUnion('type', [
	BizyMarkdownNode,
	BizyApprovalCardNode,
	BizyContentCardNode,
	BizyDraftCardNode,
	BizyMeetingPeopleCardNode,
	RecognitionDraftCardSchema,
	CommentDraftCardSchema,
	RecognitionViewCardSchema,
]);

export type BizyAssistantMessageNode = z.infer<typeof BizyAssistantMessageNode>;
export type BizyMarkdownNode = z.infer<typeof BizyMarkdownNode>;
export type BizyApprovalCardNode = z.infer<typeof BizyApprovalCardNode>;
export type BizyContentCardNode = z.infer<typeof BizyContentCardNode>;
export type BizyDraftCardNode = z.infer<typeof BizyDraftCardNode>;
export type BizyMeetingPeopleCardNode = z.infer<typeof BizyMeetingPeopleCardNode>;

/**
 * Tolerant array parser for persisted AssistantMessage documents.
 *
 * History rehydration ran the whole `assistant_message` array through a
 * strict `z.array(BizyAssistantMessageNode)`. A single unrecognized node
 * `type` (legacy data, forward-compat from a newer backend, malformed
 * payload) failed the array, then `BizyUserMessage.safeParse` failed,
 * then `BizyUserConversationDetail.safeParse` failed, and the conversation
 * silently disappeared from history (`if (!parsed.success) return;` in
 * `refetchConversation` / `loadConversation`).
 *
 * This transform drops invalid nodes individually instead — a corrupt or
 * unknown card no longer takes the rest of the conversation down with it.
 */
export const BizyAssistantMessageNodeListSchema = z
	.array(z.unknown())
	.transform((raw) => raw.map((item) => BizyAssistantMessageNode.safeParse(item)).flatMap((result) => (result.success ? [result.data] : [])));

// ─── Index (list) item ───────────────────────────────────────────────────────

export const BizyUserConversation = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	messageCount: z.number().int(),
	// Nullable because proactive (assistant-first) conversations have no user
	// message yet. The backend falls back to bizy_response in that case, but
	// keep this defensive — an empty conversation row should never break the
	// dropdown / history list.
	firstUserMessage: z.string().nullable(),
	firstMessageAt: isoDatetimeToDate,
	lastMessageAt: isoDatetimeToDate,
	conversationTitle: z.string().nullable().optional(),
});

export type BizyUserConversation = z.infer<typeof BizyUserConversation>;

// ─── Detail message ──────────────────────────────────────────────────────────

export const BizyUserMessage = z.object({
	id: z.string(),
	// Nullable because proactive (assistant-first) ChatHistory rows have no
	// user message — Bizy started the conversation. The hydration logic in
	// useBizyGlobalChat skips pushing a user bubble when this is null/empty.
	userMessage: z.string().nullable(),
	bizyResponse: z.string(),
	createdAt: isoDatetimeToDate,
	emotion: z.string().nullable(),
	feedbackType: z.enum(['positive', 'negative']).nullable(),
	feedbackCategory: z.string().nullable(),
	feedbackText: z.string().nullable(),
	pagePath: z.string().nullable(),
	assistantMessage: BizyAssistantMessageNodeListSchema.nullable().optional(),
});

export type BizyUserMessage = z.infer<typeof BizyUserMessage>;

// ─── Detail wrapper ──────────────────────────────────────────────────────────

export const BizyUserConversationDetail = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	messages: z.array(BizyUserMessage),
});

export type BizyUserConversationDetail = z.infer<typeof BizyUserConversationDetail>;

// ─── Fixtures ────────────────────────────────────────────────────────────────

export const bizyUserConversationFixture = (overrides: Partial<BizyUserConversation> = {}): BizyUserConversation => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	messageCount: fixture.int(1, 10),
	firstUserMessage: fixture.description('conversation') as string | null,
	firstMessageAt: fixture.pastDate(7),
	lastMessageAt: fixture.pastDate(1),
	conversationTitle: null,
	...overrides,
});

export const bizyUserMessageFixture = (overrides: Partial<BizyUserMessage> = {}): BizyUserMessage => ({
	id: fixture.id(),
	userMessage: fixture.description('question'),
	bizyResponse: fixture.description('answer'),
	createdAt: fixture.pastDate(1),
	emotion: 'curious',
	feedbackType: null,
	feedbackCategory: null,
	feedbackText: null,
	pagePath: '/admin/reports/participation',
	...overrides,
});

export const bizyUserConversationDetailFixture = (overrides: Partial<BizyUserConversationDetail> = {}): BizyUserConversationDetail => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	messages: [bizyUserMessageFixture(), bizyUserMessageFixture()],
	...overrides,
});
